import type { QueryClient } from "@tanstack/react-query";

/**
 * Live document state without page reloads.
 *
 * The QueryClient is configured with `staleTime: Infinity`, so nothing refreshes unless a mutation
 * invalidates it. That is why a cancel, a trash move or a signer signing in another tab left the
 * screen stale. Three rules fix it everywhere:
 *   1. every document mutation calls `invalidateDocumentQueries` (one list of keys, not per page);
 *   2. document queries spread `LIVE_QUERY_OPTIONS` (refresh on tab focus / reconnect);
 *   3. while something on screen is still waiting for a signature, poll every LIVE_INTERVAL_MS.
 *      Polling stops by itself once everything is settled, and React Query pauses it in background tabs.
 */

/** Statuses that cannot change on their own any more (document or signer). */
const FINAL = new Set(["completed", "concluded", "cancelled", "expired", "declined", "signed", "imported"]);

/** Statuses that another party (a signer, the clock) can change at any moment: worth polling. */
const AWAITING = new Set(["pending", "pendingsignature", "pending_signature", "in_progress", "inprogress", "sent", "viewed"]);

export function isFinalStatus(status?: string | null): boolean {
  return FINAL.has((status || "").toLowerCase());
}

export function isAwaitingSignature(status?: string | null): boolean {
  return AWAITING.has((status || "").toLowerCase());
}

/** The sender can still act (cancel, remind) on a document in one of these states. */
export function isDocumentActive(status?: string | null): boolean {
  return isAwaitingSignature(status);
}

export const LIVE_INTERVAL_MS = 10_000;

/** Query keys (prefixes) that render document state somewhere in the app. */
const DOCUMENT_QUERY_KEYS: readonly (readonly string[])[] = [
  ["/api/documents"],          // list, "crm" variant and every [id, "details"/"audit"] query
  ["/api/documents/trash"],
  ["/api/documents/personal"],
  ["/api/stats"],
  ["/api/signatures"],
  ["/api/notifications/recent"],
  ["/api/notifications"],
  ["/api/documents/check-limit"],   // monthly quota shown on the buttons and banners
];

export function invalidateDocumentQueries(queryClient: QueryClient): Promise<unknown> {
  return Promise.all(DOCUMENT_QUERY_KEYS.map((queryKey) => queryClient.invalidateQueries({ queryKey: [...queryKey] })));
}

export const LIVE_QUERY_OPTIONS = {
  staleTime: 5_000,
  refetchOnWindowFocus: true,
  refetchOnReconnect: true,
} as const;

type WithStatus = { status?: string | null };

/** `refetchInterval` for a query whose data holds documents/signers: poll only while one still awaits action. */
export function pollWhilePending<T>(pick: (data: T | undefined) => WithStatus[] | undefined) {
  return (query: { state: { data: T | undefined } }): number | false => {
    const items = pick(query.state.data);
    if (!items || items.length === 0) return false;
    return items.some((item) => isAwaitingSignature(item.status)) ? LIVE_INTERVAL_MS : false;
  };
}

/** `refetchInterval` for a single document query. */
export const pollWhileDocumentPending = pollWhilePending<WithStatus | null>((doc) => (doc ? [doc] : undefined));

/** `refetchInterval` for a list response: an array, a wrapped `{ data: [] }` or a page `{ items, total }`. */
export const pollWhileListPending = pollWhilePending<unknown>((data) => {
  const wrapped = data as { data?: unknown; items?: unknown } | undefined;
  const list = Array.isArray(data) ? data : wrapped?.items ?? wrapped?.data;
  return Array.isArray(list) ? (list as WithStatus[]) : undefined;
});
